import type { DateFormat, TimeFormat } from '@uchat-team-management/shared';
import { createSignal } from 'solid-js';

export interface DateTimePrefs {
  dateFormat: DateFormat | null;
  timeFormat: TimeFormat | null;
  timezone: string | null;
}

// Module-level signal: reads inside JSX are tracked, so every call site
// re-renders when AuthProvider syncs new preferences. null = Auto.
const [prefs, setPrefs] = createSignal<DateTimePrefs>({
  dateFormat: null,
  timeFormat: null,
  timezone: null,
});
export const setDateTimePrefs = setPrefs;

// Intl options don't control field order — the locale does, so each preset pins one.
const DATE_STYLES: Record<DateFormat, [string, Intl.DateTimeFormatOptions]> = {
  'DD/MM/YYYY': ['en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }],
  'MM/DD/YYYY': ['en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }],
  'YYYY-MM-DD': ['en-CA', { day: '2-digit', month: '2-digit', year: 'numeric' }],
  'DD MMM YYYY': ['en-GB', { day: 'numeric', month: 'short', year: 'numeric' }],
};
const TIME_STYLES: Record<TimeFormat, Intl.DateTimeFormatOptions> = {
  '24h': { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  '12h': { hour: 'numeric', minute: '2-digit', hour12: true },
};
// Auto mirrors toLocaleTimeString() defaults; Auto date options are just {}.
const AUTO_TIME: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
};

const cache = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (
  kind: 'date' | 'time',
  preset: string | null,
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
  fallbackTimeZone?: string,
): Intl.DateTimeFormat => {
  const timeZone = prefs().timezone ?? fallbackTimeZone;
  const key = `${kind}|${preset ?? ''}|${timeZone ?? ''}`;
  let formatter = cache.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    } catch {
      // Stored zone unknown to this browser's ICU — degrade to browser timezone.
      formatter = new Intl.DateTimeFormat(locale, options);
    }
    cache.set(key, formatter);
  }
  return formatter;
};

const dateFormatter = (fallbackTimeZone?: string): Intl.DateTimeFormat => {
  const preset = prefs().dateFormat;
  const [locale, options] = preset ? DATE_STYLES[preset] : [undefined, {}];
  return getFormatter('date', preset, locale, options, fallbackTimeZone);
};

const timeFormatter = (fallbackTimeZone?: string): Intl.DateTimeFormat => {
  const preset = prefs().timeFormat;
  return getFormatter(
    'time',
    preset,
    undefined,
    preset ? TIME_STYLES[preset] : AUTO_TIME,
    fallbackTimeZone,
  );
};

/** Optional per-call zone used only while the user's timezone preference is Auto. */
export interface FormatOptions {
  fallbackTimeZone?: string;
}

/** The IANA zone the format helpers will actually use, for "times in …" labels. */
export const effectiveTimeZone = (opts: FormatOptions = {}): string =>
  prefs().timezone ?? opts.fallbackTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

const parse = (iso: string): Date | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : new Date(t);
};

/** Date part of an instant per the user's preferences; '' for empty/invalid input. */
export const formatDate = (iso: string, opts: FormatOptions = {}): string => {
  const d = parse(iso);
  return d ? dateFormatter(opts.fallbackTimeZone).format(d) : '';
};

/** A calendar date (YYYY-MM-DD) in the user's date format, never shifted by timezone. */
export const formatCalendarDate = (ymd: string): string => {
  const d = parse(`${ymd}T00:00:00Z`);
  if (!d) return '';
  const preset = prefs().dateFormat;
  const [locale, options] = preset ? DATE_STYLES[preset] : [undefined, {}];
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(d);
};

/** Date + time of an instant per the user's preferences; '' for empty/invalid input. */
export const formatDateTime = (iso: string, opts: FormatOptions = {}): string => {
  const d = parse(iso);
  return d
    ? `${dateFormatter(opts.fallbackTimeZone).format(d)}, ${timeFormatter(opts.fallbackTimeZone).format(d)}`
    : '';
};

/** Time of day only (charts, timelines), same preference rules as formatDateTime. */
export const formatTime = (iso: string, opts: FormatOptions = {}): string => {
  const d = parse(iso);
  return d ? timeFormatter(opts.fallbackTimeZone).format(d) : '';
};
